import { useState, useEffect, useRef, useContext, memo, useMemo, useCallback, forwardRef } from 'react';
import PropTypes from 'prop-types';
import { AppContext } from './AppContext.tsx';
import ChevronDownIcon from './icons/chevrondown.svg?react'
import BrainIcon from './icons/brain.svg?react'

/**
 * ThinkingModeSelector - A dropdown component for selecting thinking mode levels
 * 
 * This component provides a user interface for selecting thinking intensity levels
 * for AI models that support reasoning. It displays available thinking modes 
 * (Off, Low, Medium, Max, Dynamic) based on the selected model's capabilities.
 * 
 * @example
 * // Basic usage in main input area
 * <ThinkingModeSelector />
 * 
 * @component
 */
const ThinkingModeSelector = ({ 
  position = 'top-right'
}) => {
  // Context integration
  const context = useContext(AppContext);
  const selectedModel = context?.selectedModel;
  const thinkingMode = context?.thinkingMode || 'medium';
  const setThinkingMode = context?.setThinkingMode;
  const thinkingModeSelectorRef = context?.thinkingModeSelectorRef;

  // Dropdown state management
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const localRef = useRef(null);
  const itemRefs = useRef([]);
  const dropdownRef = useRef(null);

  // Get available thinking modes from the selected model
  const availableModes = useMemo(() => {
    if (!selectedModel?.thinking_config?.supported) {
      return [];
    }

    const presets = selectedModel.thinking_config.presets || {};
    const modes = [];

    // Define order and labels for thinking modes
    const modeOrder = ['max', 'medium', 'low', 'dynamic', 'off'];
    const isAdaptive = selectedModel?.thinking_config?.thinking_type === 'adaptive';
    const modeLabels = isAdaptive
      ? { off: 'Off', low: 'Light', medium: 'Balanced', max: 'Maximum', dynamic: 'Dynamic' }
      : { off: 'Off', low: 'Low', medium: 'Medium', max: 'Max', dynamic: 'Dynamic' };

    for (const mode of modeOrder) {
      if (presets[mode]?.enabled) {
        modes.push({
          value: mode,
          label: modeLabels[mode],
          enabled: true
        });
      }
    }

    return modes;
  }, [selectedModel]);

  // Check if thinking mode is supported by current model
  const isSupported = useMemo(() => {
    return selectedModel?.thinking_config?.supported && availableModes.length > 0;
  }, [selectedModel, availableModes]);

  // Check if thinking mode is customizable (not fixed like Grok)
  const isCustomizable = useMemo(() => {
    return selectedModel?.thinking_config?.customizable !== false;
  }, [selectedModel]);

  // Determine button state
  const buttonState = useMemo(() => {
    if (!isSupported) return 'disabled';
    if (!isCustomizable && thinkingMode !== 'off') return 'fixed'; // Fixed thinking mode (like Grok)
    if (isOpen) return 'active';
    if (thinkingMode !== 'off') return 'on';
    return 'passive';
  }, [isSupported, isCustomizable, isOpen, thinkingMode]);

  // Handle mode selection
  const selectMode = useCallback((mode) => {
    if (setThinkingMode) {
      setThinkingMode(mode.value);
    }
    setIsOpen(false);
  }, [setThinkingMode]);

  // Register toggle function for keyboard shortcuts
  useEffect(() => {
    if (thinkingModeSelectorRef?.current) {
      thinkingModeSelectorRef.current.toggle = () => setIsOpen(prev => !prev);
    }
  }, [thinkingModeSelectorRef]);

  // Keyboard navigation setup - focus currently selected mode when opened
  useEffect(() => {
    if (isOpen) {
      // Find the index of the currently selected thinking mode
      const currentModeIndex = availableModes.findIndex(mode => mode.value === thinkingMode);
      // Focus the current mode if found, otherwise focus the first item
      setHighlightedIndex(currentModeIndex >= 0 ? currentModeIndex : 0);
      setTimeout(() => dropdownRef.current?.focus(), 0);
    } else {
      setHighlightedIndex(-1);
    }
  }, [isOpen, availableModes, thinkingMode]);

  useEffect(() => {
    if (highlightedIndex >= 0 && itemRefs.current[highlightedIndex]) {
      itemRefs.current[highlightedIndex].scrollIntoView({
        block: 'nearest',
        inline: 'nearest'
      });
    }
  }, [highlightedIndex]);

  // Handle clicks outside the dropdown
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (localRef.current && !localRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside, { passive: true });
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (e) => {
      const last = availableModes.length - 1;
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex(i => i < last ? i + 1 : 0);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex(i => i > 0 ? i - 1 : last);
          break;
        case 'Enter':
          e.preventDefault();
          if (highlightedIndex >= 0) {
            selectMode(availableModes[highlightedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          setIsOpen(false);
          break;
      }
    };

    const dr = dropdownRef.current;
    dr?.addEventListener('keydown', onKeyDown);
    return () => dr?.removeEventListener('keydown', onKeyDown);
  }, [isOpen, highlightedIndex, availableModes, selectMode]);

  // Get current mode label
  const currentModeLabel = useMemo(() => {
    const mode = availableModes.find(m => m.value === thinkingMode);
    return mode?.label || 'Off';
  }, [availableModes, thinkingMode]);

  // Determine dropdown position classes
  const getPosition = useMemo(() => (pos) => {
    const positions = {
      'top-right': 'bottom-full right-0 mb-2',
      'top-left': 'bottom-full left-0 mb-2',
    };
    return positions[pos] || 'bottom-full right-0 mb-2';
  }, []);

  // Brain icon - color based on button state
  const lightBulbIcon = useMemo(() => {
    const baseClasses = "h-4 w-4 transition-colors duration-300";

    // Determine color based on button state
    let iconColor;
    if (buttonState === 'disabled') {
      iconColor = '--color-secondary';
    } else if (buttonState === 'on' || buttonState === 'active' || buttonState === 'fixed') {
      // Show primary color when thinking is on, dropdown is open, OR thinking is fixed
      iconColor = '--color-primary';
    } else {
      // Show dimmer color when thinking is off
      iconColor = '--color-secondary';
    }

    return <BrainIcon className={`${baseClasses} ${iconColor}`} style={{ color: `var(${iconColor})` }} />;
  }, [buttonState, thinkingMode]);

  // Main trigger button
  const triggerButton = useMemo(() => {
    const isDisabled = !isSupported || !isCustomizable;
    const isUnsupported = !isSupported;
    const isFixed = isSupported && !isCustomizable;

    return (
      <button
        ref={thinkingModeSelectorRef}
        aria-label={`Select thinking mode - currently ${currentModeLabel}`}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        className={`box-border w-8 h-8 rounded-full bg-bg-card flex items-center justify-center border border-border-default hover:bg-bg-hover transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
          isDisabled 
            ? `cursor-not-allowed ${isUnsupported ? 'opacity-50' : ''}` 
            : 'hover:cursor-pointer'
        }`}
        onClick={isDisabled ? null : (e) => {
          e.stopPropagation();
          setIsOpen((prev) => !prev);
        }}
        disabled={isDisabled}
        title={
          !isSupported 
            ? 'Thinking mode not supported by this model'
            : !isCustomizable
            ? 'Thinking mode is fixed for this model' 
            : `Thinking mode: ${currentModeLabel}`
        }
      >
        {lightBulbIcon}
      </button>
    );
  }, [isSupported, isCustomizable, isOpen, buttonState, currentModeLabel, lightBulbIcon, thinkingModeSelectorRef]);

  // Mode items list
  const modeItems = useMemo(() => (
    <div 
      role="listbox"
      aria-label="Available thinking modes"
      className="flex flex-col hover:cursor-pointer max-h-64 overflow-y-auto overflow-x-hidden custom-scrollbar p-spacing-1 w-full">
      {availableModes.map((mode, idx) => (
        <ThinkingModeItem
          key={mode.value}
          ref={el => itemRefs.current[idx] = el}
          mode={mode}
          isSelected={thinkingMode === mode.value}
          isHighlighted={idx === highlightedIndex}
          onSelect={selectMode}
          onHighlight={() => setHighlightedIndex(idx)}
        />
      ))}
    </div>
  ), [availableModes, thinkingMode, highlightedIndex, selectMode]);

  // The dropdown container
  const dropdown = useMemo(() => (
    <div
      ref={dropdownRef}
      tabIndex={-1}
      className={`
        ${getPosition(position)}
        focus:outline-none
        focus:ring-0
        absolute 
        text-primary
        bg-bg-dropdown
        rounded-lg
        flex
        flex-col
        w-48
        border border-border-default
        transition-all
        duration-300
        ease-out
        ${isOpen ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-4 scale-95 pointer-events-none'}
      `}
    >
      {modeItems}
    </div>
  ), [isOpen, getPosition, position, modeItems]);

  // Always render the component, but it will be disabled if not supported

  return (
    <div ref={localRef} className="relative inline-block">
      {triggerButton}
      {dropdown}
    </div>
  );
};

// Individual thinking mode item component
const ThinkingModeItem = memo(forwardRef(({
  mode,
  isSelected,
  isHighlighted,
  onSelect,
  onHighlight
}, ref) => (
  <button
    role="option"
    aria-selected={isSelected}
    ref={ref}
    onClick={() => onSelect(mode)}
    onMouseEnter={onHighlight}
    className={`
      w-full flex flex-row items-center justify-between px-spacing-3 py-spacing-2
      cursor-pointer rounded-lg transition-all duration-200
      focus:outline-none focus-visible:ring-2 focus-visible:ring-accent text-left
      ${isHighlighted ? 'bg-bg-hover' : 'hover:bg-bg-hover'}
    `}
  >
    <span className="text-primary">
      {mode.label}
    </span>
    {isSelected && (
      <div className="w-2 h-2 bg-accent rounded-full" />
    )}
  </button>
)));

ThinkingModeItem.displayName = 'ThinkingModeItem';

ThinkingModeSelector.propTypes = {
  /** Position of the dropdown relative to the trigger button */
  position: PropTypes.string
};

export default memo(ThinkingModeSelector);
